<template>
  <div class="channels-table">
    <h1>Canais</h1>
    <v-card class="mt-3">
      <v-card-title>
        <span class="table-card-title">Disparos</span>
      </v-card-title>
      <v-data-table
        :items="tableData"
        :headers="headers"
        :loading="loading"
        hide-default-footer
        disable-pagination
        no-data-text="Não existem dados a serem exibidos. Selecione outro período ou entre em contato com o suporte pelo chat."
      >
        <template v-slot:item="{ item }">
          <tr>
            <td v-if="item.hasChannel">
              <router-link :to="{ path: item.path }">
                <span class="link">{{ item.channel }}</span>
              </router-link>
            </td>
            <td v-else style="text-decoration: underline">{{ item.channel }}</td>
            <td>{{ locale(item.sent) }}</td>
            <td>{{ locale(item.received) }}</td>
            <td>{{ item.delivery > 0 ? `${locale(item.delivery, 2)}%` : item.delivery }}</td>

            <v-tooltip top max-width="300px" :disabled="item.disableTooltip || !item.hasChannel">
              <template v-slot:activator="{ on }">
                <td v-on="on">
                  {{ item.price > 0 ? `R$ ${locale(item.price, 2)}` : item.price }}
                </td>
              </template>

              <span> Esse canal não possui custos por disparo. </span>
            </v-tooltip>
            <td>{{ item.revenue > 0 ? `R$ ${locale(item.revenue, 2)}` : item.revenue }}</td>
          </tr>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import { assertArray, localeString } from '@/config/helpers.js'
import { QUERY_GENERAL_RESULTS_CHANNELS } from '@/modules/campanhas/resultados-gerais/graphql'
import { mapState } from 'vuex'
export default {
  name: 'ChannelsTable',
  components: {},
  props: {
    campaignIds: {
      type: Array,
      default: () => [],
    },
    productIds: {
      type: Array,
      default: () => [],
    },
    channels: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    locale: localeString,
    campaignsChannelsMetrics: [],
  }),
  computed: {
    ...mapState({
      stateFilters: (state) => state.filters,
    }),
    skip() {
      return !this.defaultQueryVariables || !this.defaultQueryVariables.initialDate || !this.defaultQueryVariables.finalDate || !this.campaignIds.length
    },
    loading() {
      return this.$apollo.queries.campaignsChannelsMetrics.loading
    },
    headers() {
      return [
        {
          text: 'Canal',
          value: 'channel',
          width: '16%',
        },
        {
          text: 'Enviados',
          value: 'sent',
          width: '16%',
        },
        {
          text: 'Recebidos',
          value: 'received',
          width: '16%',
        },
        {
          text: 'Taxa de entrega',
          value: 'delivery',
          width: '16%',
        },
        {
          text: 'Custo',
          value: 'price',
          width: '16%',
        },
        {
          text: 'Faturamento',
          value: 'revenue',
          width: '16%',
        },
      ]
    },
    defaultQueryVariables() {
      const { data } = this.stateFilters
      const [initialDate = '', finalDate = ''] = assertArray(data)
      const variables = {
        initialDate,
        finalDate,
        campaignIds: this.campaignIds,
        productIds: this.productIds,
        channels: this.channels,
      }

      return variables
    },
    tableData() {
      if (!this.campaignsChannelsMetrics || !this.campaignsChannelsMetrics.general_metrics || !this.campaignsChannelsMetrics.analytic_metrics) return []
      const selectedChannels = this.stateFilters.channels
      const channels = Object.keys(this.campaignsChannelsMetrics.general_metrics.channels).filter((channel) => channel !== '__typename')
      const tableData = channels.map((channel) => {
        const sent = this.campaignsChannelsMetrics.general_metrics.channels[channel].sent.messages
        const received = this.campaignsChannelsMetrics.general_metrics.channels[channel].received.messages
        const delivery = (received * 100) / sent
        const price = this.campaignsChannelsMetrics.analytic_metrics.channels[channel].price
        const revenue = this.campaignsChannelsMetrics.client_metrics.channels[channel].received_and_bought.revenue
        const hasChannel = selectedChannels.includes(channel.toUpperCase())

        return {
          channel: this.formatChannelName(channel),
          hasChannel,
          received: received > 0 && hasChannel ? received : '-',
          sent: sent > 0 && hasChannel ? sent : '-',
          delivery: delivery > 0 && hasChannel ? delivery : '-',
          price: price > 0 && hasChannel ? price : '-',
          revenue: revenue > 0 && hasChannel ? revenue : '-',
          path: this.getPath(channel),
          disableTooltip: channel !== 'push' && channel !== 'whatsapp',
        }
      })
      return tableData
    },
  },
  apollo: {
    campaignsChannelsMetrics: {
      query: QUERY_GENERAL_RESULTS_CHANNELS,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      update({ campaignsChannelsMetrics }) {
        return campaignsChannelsMetrics
      },
      variables() {
        return this.defaultQueryVariables
      },
      context() {
        return {
          uri: this.$microservicesUrls['campaigns'],
        }
      },
      skip() {
        return this.skip
      },
    },
  },
  watch: {
    campaignIds: {
      deep: true,
      handler: function () {
        this.campaignsChannelsMetrics = []
        if (!this.skip) {
          const variables = {
            ...this.defaultQueryVariables,
            productIds: this.productIds,
          }
          this.$apollo.queries.campaignsChannelsMetrics.refetch({
            ...variables,
          })
        }
      },
    },
  },
  methods: {
    formatChannelName(channel) {
      if (channel === 'sms') return 'SMS'
      if (channel === 'whatsapp') return 'Whatsapp'
      if (channel === 'push') return 'Push aplicativo'
      if (channel === 'email') return 'E-mail'
    },
    getPath(channel) {
      if (channel === 'sms') return '/campanhas/sms'
      if (channel === 'whatsapp') return '/campanhas/whatsapp'
      if (channel === 'push') return '/campanhas/aplicativo'
      if (channel === 'email') return '/campanhas/email'
    },
  },
}
</script>

<style lang="scss" scoped>
.channels-table {
  & h1 {
    font-size: 26px;
    color: $cinza-d10;
    font-weight: bold;
  }
}

::v-deep .v-data-table__empty-wrapper {
  color: $cinza-l50 !important;
}
</style>
